import React, { useMemo, useState } from "react";

import {
  faExclamationCircle,
  faEye,
  faMagnifyingGlass,
  faSpinner,
  faUser,
} from "@fortawesome/pro-light-svg-icons";
import {
  ColumnOrderState,
  createColumnHelper,
  DisplayColumnDef,
  HeaderContext,
  OnChangeFn,
} from "@tanstack/react-table";
import { AxiosError } from "axios";
import { isEmpty, isFunction } from "lodash-es";
import { useNavigate } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { useWorkspaceUsers } from "src/api/taktile/queries";
import { ColumnMultiValueFilter } from "src/base-components/ColumnFilter";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { Pill, PillVariants } from "src/base-components/Pill";
import { CustomPopover } from "src/base-components/Popover";
import {
  Cell,
  ColumnVisibilityDropdown,
  Header,
  TableComp,
} from "src/base-components/Table";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { SCHEMA_TYPE_ICONS } from "src/base-components/TypeIcons";
import { BooleanFilter } from "src/base-components/tableColumnFilters/BooleanFilter";
import { DateRangeFilter } from "src/base-components/tableColumnFilters/DateRangeFilter";
import { NumericFilter } from "src/base-components/tableColumnFilters/NumericFilter";
import { OptionsFilter } from "src/base-components/tableColumnFilters/OptionsFilter";
import { StringFilter } from "src/base-components/tableColumnFilters/StringFilter";
import {
  useFilter,
  useResetURLParamsFilters,
  useURLParamsFilters,
} from "src/base-components/tableColumnFilters/useFilters";
import { EntityCaseSummaryStatusEnum } from "src/clients/entities/api";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { Assignees } from "src/entities/Assignees";
import { EntityReviewStatus } from "src/entities/EntityReviewStatus";
import { EntitySchemaEditorSidebar } from "src/entities/EntitySchemaEditorSidebar";
import {
  renderValue,
  TypeIcon,
} from "src/entities/entityView/EntityDetailsSidePane";
import { useDefaultEntityTab } from "src/entities/entityView/useEntityTabRefresh";
import { getOrderedProperties } from "src/entities/entityView/utils";
import {
  Cardinality,
  ENTITIES_PAGE_SIZE,
  EntityResource,
  EntityResourceWithReviewStatus,
  EntitySchemaProperty,
  EntitySchemaResource,
  useEntities,
} from "src/entities/queries";
import { useEntityColumnOrder } from "src/entities/useEntityColumnOrder";
import { useSchemaEditor } from "src/entities/useSchemaEditor";
import { useTrackEntityFunnel } from "src/entities/useTrackEntityFunnel";
import {
  SensitiveField,
  SensitiveFieldValue,
} from "src/entitiesEventsCommon/SensitiveField";
import { isSensitiveType } from "src/entitiesEventsCommon/sensitiveTypes";
import { RelationshipPopoverContent } from "src/eventsCatalogue/SchemaEditor/RelationshipPopoverContent";
import { Environment, useEnvironment } from "src/hooks/useEnvironment";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { getUrlToEntityDetailPage } from "src/router/urls";
import { getUserName } from "src/utils/user";

type EntitiesTableProps = {
  entitySchema: EntitySchemaResource;
  searchQuery: string;
  schemasLoading: boolean;
  environment: Environment;
};

type RelationshipHeaderProps = {
  schemaId: string;
  property: EntitySchemaProperty;
  pluralDisplayName: string;
  singularDisplayName: string;
  cardinality?: Cardinality;
};

const reviewStatusMapping = {
  [EntityCaseSummaryStatusEnum.REVIEW_REQUIRED]: "Review required",
  [EntityCaseSummaryStatusEnum.NO_PENDING_REVIEW]: "No pending review",
};
// we virtualize columns when the number of columns is greater than this threshold
// because Table uses table-fixed instead of auto
// when the number of columns is low, the leftover space will not be filled with the columns.
const COLUMN_VIRTUALIZATION_THRESHOLD = 20;

const PROPERTY_COL_MIN_SIZE = 120;
const PROPERTY_COL_MAX_SIZE = 300;
// icon + filter button + flex gaps + Header pr-2 pl-1.5
const PROPERTY_HEADER_CHROME_PX = 56;
const PROPERTY_HEADER_CHAR_PX = 7;

const computePropertyColumnSize = (displayName: string): number =>
  Math.max(
    PROPERTY_COL_MIN_SIZE,
    Math.min(
      PROPERTY_COL_MAX_SIZE,
      displayName.length * PROPERTY_HEADER_CHAR_PX + PROPERTY_HEADER_CHROME_PX,
    ),
  );

const RelationshipHeader: React.FC<RelationshipHeaderProps> = ({
  schemaId,
  property,
  pluralDisplayName,
  singularDisplayName,
  cardinality = Cardinality.ONE,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <CustomPopover
      button={
        <div className="flex h-8 min-w-0 items-center gap-0.5 pr-2">
          <TypeIcon cardinality={property._cardinality} type={property._type} />
          <p className="truncate">{property._display_name}</p>
        </div>
      }
      isOpen={isOpen}
      wrapperClassName="block w-full max-w-full"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <RelationshipPopoverContent
        cardinality={cardinality}
        pluralDisplayName={pluralDisplayName}
        propertyDisplayName={property._display_name}
        relatedSchemaId={property._rel_schema as string}
        schemaId={schemaId}
        singularDisplayName={singularDisplayName}
      />
    </CustomPopover>
  );
};

const AssigneeFilter = () => {
  const { filters, update, apply, reset, isFiltering, isApplied } = useFilter(
    "case.assignees",
    [
      {
        key: "case.assignees",
        operator: "eq",
        values: [],
      },
    ],
  );
  const filter = filters[0];

  const { orgId, workspace } = useWorkspaceContext();
  const { data: users } = useWorkspaceUsers(orgId, workspace.id, {
    include_deactivated: false,
  });

  const noAssignee = {
    key: "None",
    value: "No assignee",
  };
  const dropdownElements =
    users?.map((user) => ({
      key: user.id,
      value: getUserName(user) as string,
    })) ?? [];

  return (
    <ColumnMultiValueFilter
      defaultValue={undefined}
      headline="Filter by Assignee"
      isApplied={isApplied()}
      isFiltering={isFiltering({ key: "case.assignees" })}
      options={[noAssignee, ...dropdownElements]}
      values={filter.values}
      onApply={apply}
      onChange={(values) => update([{ ...filter, values }])}
      onResetRequest={() => reset()}
    />
  );
};

type PropertyFilterProps = {
  propertyKey: string;
  property: EntitySchemaProperty;
};

const PropertyFilter: React.FC<PropertyFilterProps> = ({
  property,
  propertyKey,
}) => {
  if (["enum", "tags"].includes(property._type) && property._values) {
    return (
      <OptionsFilter
        dataLoc={`${propertyKey}-filter`}
        displayName={property._display_name}
        operator={property._type === "enum" ? "one_of" : "contains"}
        options={property._values.map((v) => ({
          key: v._internal_name,
          value: (
            <Pill size="sm" variant={(v._color ?? "gray") as PillVariants}>
              <Pill.Text>{v._internal_name}</Pill.Text>
            </Pill>
          ),
        }))}
        property={propertyKey}
      />
    );
  }

  if (property._type === "boolean") {
    return (
      <BooleanFilter
        dataLoc={`${propertyKey}-filter`}
        displayName={property._display_name}
        property={propertyKey}
      />
    );
  }

  if (property._type === "number" || property._type === "integer") {
    return (
      <NumericFilter
        dataLoc={`${propertyKey}-filter`}
        displayName={property._display_name}
        property={propertyKey}
        type={property._type}
      />
    );
  }

  if (property._type === "string") {
    return (
      <StringFilter
        dataLoc={`${propertyKey}-filter`}
        displayName={property._display_name}
        property={propertyKey}
      />
    );
  }

  if (property._type === "date" || property._type === "datetime") {
    return (
      <DateRangeFilter
        dataLoc={`${propertyKey}-date-filter`}
        property={propertyKey}
      />
    );
  }

  return null;
};

const ReviewStatusFilter: React.FC<{
  info: HeaderContext<EntityResourceWithReviewStatus, unknown>;
}> = ({ info }) => {
  const { filters, update, apply, reset, isFiltering, isApplied } = useFilter(
    "case.status",
    [
      {
        key: "case.status",
        operator: "eq",
        values: [],
      },
    ],
  );
  const filter = filters[0];

  return (
    <EntityTableHeader
      filter={
        <ColumnMultiValueFilter
          defaultValue={undefined}
          headline="Filter by Review Status"
          isApplied={isApplied()}
          isFiltering={isFiltering({ key: "case.status" })}
          options={Object.values(EntityCaseSummaryStatusEnum).map((status) => ({
            key: status,
            value: reviewStatusMapping[status],
          }))}
          values={filter.values}
          onApply={apply}
          onChange={(values) => update([{ ...filter, values }])}
          onResetRequest={() => reset()}
        />
      }
      icon={<Icon icon={faSpinner} size="2xs" />}
      info={info}
    >
      Review Status
    </EntityTableHeader>
  );
};

// Mirrors TanStack's default column id derivation: dots in accessor keys
// become underscores (see @tanstack/table-core createColumn)
const propertyColumnId = (propertyKey: string) =>
  `properties.${propertyKey}`.replaceAll(".", "_");

type BuildColumnsOptions = {
  hasCustomOrder: boolean;
  onResetOrder: () => void;
};

const buildColumns = (
  entitySchema: EntitySchemaResource,
  environment: Environment,
  { hasCustomOrder, onResetOrder }: BuildColumnsOptions,
) => {
  const columnHelper = createColumnHelper<EntityResourceWithReviewStatus>();
  const orderedProperties = getOrderedProperties(entitySchema).filter(
    (propertyKey) => propertyKey !== "id",
  );

  const idColumn = (() => {
    if (!("id" in entitySchema.properties)) {
      return false;
    }

    const idProperty = entitySchema.properties["id"];

    return columnHelper.accessor("properties.id", {
      meta: {
        displayName: idProperty._display_name,
      },
      header: (info) => (
        <Header info={info}>
          <div className="flex items-center gap-x-0.5">
            <Icon icon={SCHEMA_TYPE_ICONS["string"]} size="2xs" />
            <Tooltip title={idProperty._display_name} overflowOnly asChild>
              <span className="truncate">{idProperty._display_name}</span>
            </Tooltip>
          </div>
        </Header>
      ),
      maxSize: PROPERTY_COL_MAX_SIZE,
      minSize: PROPERTY_COL_MIN_SIZE,
      size: computePropertyColumnSize(idProperty._display_name),
      cell: (info) => (
        <Cell className="w-24" info={info}>
          <TruncatedIdPill id={info.getValue()} variant="middle-truncated" />
        </Cell>
      ),
    });
  })();

  const reviewStatus = columnHelper.accessor("entity_case_summary.status", {
    header: (info) => {
      return <ReviewStatusFilter info={info} />;
    },
    cell: (info) => {
      return (
        <EntityReviewStatus
          status={info.getValue() as EntityCaseSummaryStatusEnum}
        />
      );
    },
    meta: {
      displayName: "Review Status",
    },
    size: 150,
  });

  const assignedTo = columnHelper.accessor("entity_case_summary.assignees", {
    header: (info) => {
      return (
        <EntityTableHeader
          filter={<AssigneeFilter />}
          icon={<Icon icon={faUser} size="2xs" />}
          info={info}
        >
          Assigned to
        </EntityTableHeader>
      );
    },
    cell: (info) => <Assignees assignees={info.getValue() ?? []} />,
    meta: {
      displayName: "Assigned to",
    },
    size: 120,
  });

  const entityColumns = orderedProperties.map((key) => {
    const property = entitySchema.properties[key];

    return columnHelper.accessor(`properties.${key}`, {
      meta: {
        displayName: property._display_name,
        reorderable: true,
      },
      header: (info) => {
        const isRelationship = property._type === "relation";
        return isRelationship ? (
          <RelationshipHeader
            cardinality={property._cardinality}
            pluralDisplayName={entitySchema._display_name_plural}
            property={property}
            schemaId={entitySchema._id}
            singularDisplayName={entitySchema._display_name_singular}
          />
        ) : (
          <EntityTableHeader
            filter={
              !property._sensitive && (
                <PropertyFilter property={property} propertyKey={key} />
              )
            }
            icon={
              <TypeIcon
                cardinality={property._cardinality}
                type={property._type}
              />
            }
            info={info}
          >
            {property._display_name}
          </EntityTableHeader>
        );
      },
      cell: (info) => {
        const isSensitive =
          isSensitiveType(property._type) && property._sensitive;
        const value = renderValue({
          property,
          propertyValue: info.row.original.properties[key],
          isWindowPill: true,
          foreginKeyToPrimaryProperty: info.row.original._fk_pp,
          maxDisplayLines: 1,
          environment,
        });
        if (!isSensitive) return <Cell info={info}>{value}</Cell>;

        const rawValue = info.row.original.properties[key];
        return (
          <Cell className="max-w-60" info={info}>
            <SensitiveField
              displayJsonFormat={false}
              propertyName={property._display_name}
              value={rawValue as SensitiveFieldValue}
            />
          </Cell>
        );
      },
      maxSize: PROPERTY_COL_MAX_SIZE,
      minSize: PROPERTY_COL_MIN_SIZE,
      size: computePropertyColumnSize(property._display_name),
    });
  });

  const detailsColumn = columnHelper.display({
    id: "fields.options",
    meta: {
      sticky: "right",
    },
    header: (info) => (
      <div className="flex h-8 justify-end bg-white pr-1.5">
        <ColumnVisibilityDropdown
          footerHint={
            hasCustomOrder
              ? "You have a custom column order. Reset also restores the default order"
              : undefined
          }
          min={3}
          table={info.table}
          onReset={() => {
            info.table.resetColumnVisibility();
            onResetOrder();
          }}
        />
      </div>
    ),
    size: 54,
    cell: (info) => (
      <Cell
        className="items-center justify-end group-hover:bg-gray-50"
        info={info}
      >
        <div className="opacity-0 group-hover:opacity-100">
          <Icon icon={faEye} size="xs" />
        </div>
      </Cell>
    ),
  });

  return [
    idColumn,
    ...entityColumns,
    reviewStatus,
    assignedTo,
    detailsColumn,
  ].filter(Boolean) as DisplayColumnDef<EntityResource, unknown>[];
};

export const EntitiesTable: React.FC<EntitiesTableProps> = ({
  entitySchema,
  searchQuery,
  schemasLoading,
  environment,
}) => {
  const { orgId, workspace } = useWorkspaceContext();
  const navigate = useNavigate();
  const [env] = useEnvironment();
  const [isEditing] = useSchemaEditor();
  const defaultEntityTab = useDefaultEntityTab();
  const { trackDetailOpened } = useTrackEntityFunnel(entitySchema._id);

  const filters = useURLParamsFilters();
  const resetFilters = useResetURLParamsFilters();

  const { data, isLoading, isError, error, fetchNextPage, isFetching } =
    useEntities({
      schema: entitySchema._id,
      env,
      filters: {
        ...filters,
        "filter[id][eq]": searchQuery.length > 0 ? searchQuery : undefined,
      },
      limit: ENTITIES_PAGE_SIZE,
    });

  const entities = useMemo(
    () =>
      data?.pages.flatMap(
        (page) => page.entities as EntityResourceWithReviewStatus[],
      ) ?? [],
    [data?.pages],
  );

  const showEmptyState = !isLoading && !isError && !entities.length;
  const isFilteredEmptyState = showEmptyState && !isEmpty(filters);

  const { orderedPropertyKeys, hasCustomOrder, setOrder, resetOrder } =
    useEntityColumnOrder(entitySchema);

  const columns = useMemo(
    () =>
      buildColumns(entitySchema, environment, {
        hasCustomOrder,
        onResetOrder: resetOrder,
      }),
    [entitySchema, environment, hasCustomOrder, resetOrder],
  );

  // Render order of leaf columns; unlisted columns (review status, assigned
  // to, options) append at the end in definition order
  const columnOrder = useMemo(
    () => [
      propertyColumnId("id"),
      ...orderedPropertyKeys.map(propertyColumnId),
    ],
    [orderedPropertyKeys],
  );

  const handleColumnOrderChange: OnChangeFn<ColumnOrderState> = (updater) => {
    const nextOrder = isFunction(updater) ? updater(columnOrder) : updater;
    const idToPropertyKey = new Map(
      orderedPropertyKeys.map((key) => [propertyColumnId(key), key] as const),
    );
    setOrder(nextOrder.flatMap((id) => idToPropertyKey.get(id) ?? []));
  };

  return (
    <FloatingWindowsProvider>
      <div
        className={twJoin(
          "m-5 min-w-[300px] overflow-hidden rounded-lg border border-gray-200 bg-white p-4",
          isEditing && "w-[calc(100%-640px)]",
        )}
      >
        <TableComp
          columnOrder={columnOrder}
          columns={columns}
          data={entities ?? []}
          dataLoc="entities-table"
          frameClassName={twJoin(
            "min-h-0 w-full flex-1 pt-0",
            (showEmptyState || isError) && "h-auto",
          )}
          getRowId={(row) => row._id}
          headerPropsGetter={() => ({
            classNameOverrides: "z-40",
          })}
          isFetchingNextPage={isFetching}
          isLoading={isLoading || schemasLoading}
          rowClassName="hover:bg-gray-50 cursor-pointer"
          rowPropsGetter={(row) => ({
            onClick: () => {
              const entityId = row.original.properties.id as string;
              trackDetailOpened(entityId);
              navigate(
                getUrlToEntityDetailPage(
                  orgId,
                  workspace.id,
                  row.original._schema,
                  entityId,
                  defaultEntityTab,
                ),
              );
            },
          })}
          uiStoreKey={`${workspace.id}_${entitySchema._id}_entities_table_visible_columns`}
          variant="compact"
          virtualizeColumns={columns.length > COLUMN_VIRTUALIZATION_THRESHOLD}
          enableColumnReordering
          onColumnOrderChange={handleColumnOrderChange}
          onScrolledToTheBottom={fetchNextPage}
        />
        {isError && <EntitiesTableErrorState error={error} />}
        {showEmptyState && (
          <div className="h-96">
            {isFilteredEmptyState ? (
              <EmptyState
                actionButton={{
                  label: "Clear filters",
                  onClick: resetFilters,
                }}
                description="There are no results matching your current filters. Try changing the filters to see relevant results."
                headline="No matches for the current filters"
                icon={faMagnifyingGlass}
              />
            ) : (
              <EmptyState
                description={`No ${entitySchema._display_name_plural.toLowerCase()} exist yet in ${
                  env === "live" ? "production" : "sandbox"
                } environment.`}
                headline={`No ${entitySchema._display_name_plural.toLowerCase()} found`}
                icon={faMagnifyingGlass}
              />
            )}
          </div>
        )}
      </div>
      {isEditing && (
        <div className="fixed top-[100px] right-0 z-10 h-[calc(100vh-100px)]">
          <EntitySchemaEditorSidebar schema={entitySchema} />
        </div>
      )}
    </FloatingWindowsProvider>
  );
};

const EntityTableHeader: React.FC<{
  info: HeaderContext<EntityResourceWithReviewStatus, unknown>;
  icon: React.ReactNode;
  filter: React.ReactNode;
  children: React.ReactNode;
}> = ({ info, icon, filter, children }) => {
  return (
    <Header info={info}>
      <div className="flex min-w-0 items-center gap-x-0.5">
        {icon}
        <Tooltip title={children} overflowOnly asChild>
          <div className="truncate">{children}</div>
        </Tooltip>
        {filter}
      </div>
    </Header>
  );
};

const EntitiesTableErrorState: React.FC<{
  error?: AxiosError;
}> = ({ error }) => {
  const resetFilters = useResetURLParamsFilters();
  const [environment, setEnvironment] = useEnvironment();
  const filters = useURLParamsFilters();

  const isLiveEnvironment = environment === "live";
  const isNoPermissionError = error?.response?.status === 403;

  if (isNoPermissionError && isLiveEnvironment) {
    return (
      <div className="h-96">
        <EmptyState
          actionButton={{
            label: "Go to Sandbox",
            onClick: () => setEnvironment("sandbox"),
          }}
          description="You don’t have permission to view data in the live environment."
          headline="Error loading entities"
          icon={faExclamationCircle}
          variant="error"
        />
      </div>
    );
  }

  if (isNoPermissionError && !isLiveEnvironment) {
    return (
      <div className="h-96">
        <EmptyState
          description="You don’t have permission to view data."
          headline="Error loading entities"
          icon={faExclamationCircle}
          variant="error"
        />
      </div>
    );
  }

  return (
    <div className="h-96">
      <EmptyState
        actionButton={
          !isEmpty(filters)
            ? {
                label: "Clear filters",
                onClick: resetFilters,
              }
            : undefined
        }
        description={`We're having trouble loading the entities.${
          !isEmpty(filters) ? " Try to clear the filters." : ""
        }`}
        headline="Error loading entities"
        icon={faExclamationCircle}
        variant="error"
      />
    </div>
  );
};
